import React, { useState, useEffect, useRef } from "react";
import heroBg from './hero-bg.webp'; 
import { hover } from "@testing-library/user-event/dist/hover";

const ACCENT = "#0d5e1b";
const ACCENT2 = "#b8b8b8";

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  html { 
    overflow-y: scroll;
    scroll-behavior: smooth;
  }

  body {
    font-family: 'Outfit', sans-serif;
    background: #ffffff;
    overflow-x: hidden;
    color: #111;
  }

  /* --- NAVIGARE --- */
  nav {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 40px;
    height: 60px;
    background: transparent;
    transition: all 0.5s ease;
  }

  nav.scrolled {
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  }

  .logo-container {
    cursor: pointer;
    display: flex;
    flex-direction: column;
    width: 170px;
    text-decoration: none;
  }

  .logo-main {
    font-size: 30px;
    line-height: .5;
    display: flex;
    justify-content: space-between;
    color: #000000; /* Alb pe fundal transparent */
    margin-top: 6px;
    transition: color 0.4s;
  }

  nav.scrolled .logo-main { color: #111; }

  .logo-sub {
    font-size: 14px;
    text-transform: lowercase;
    text-align: justify;
    text-align-last: justify;
    letter-spacing: 0.05em;
    color: #000000;
    margin-top: 3px;
    transition: color 0.4s;
    display: block;
  }

  nav.scrolled .logo-sub { color: #666; }

  .nav-links {
    display: flex;
    gap: 35px;
    list-style: none;
    align-items: center;
  }

  .nav-links a { 
    text-decoration: none;
    color: #000000;
    font-weight: 500;
    font-size: 18px;
    cursor: pointer;
    transition: color 0.4s;
  }

  nav.scrolled .nav-links a { color: #111; }

  /* --- BURGER --- */
  .burger {
    display: none;
    flex-direction: column;
    gap: 6px;
    cursor: pointer;
    z-index: 1100;
  }

  .burger span { width: 28px; height: 2px; background: #ffffff; transition: 0.3s; }
  nav.scrolled .burger span { background: #000; }

  .mobile-menu {
    position: fixed;
    inset: 0;
    background: #ebebeb;
    z-index: 1050;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 60px;
    transform: translateX(100%);
    transition: 0.5s ease;
  }

  .mobile-menu.open { transform: translateX(0); }
  .mobile-menu a { font-size: 26px; color: #111; text-decoration: none; font-weight: 800; cursor: pointer; }

  /* --- PAGINI --- */
  .page-section {
    min-height: 100vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 120px 24px;
    position: relative;
  }

  .hero-title {
    font-size: clamp(34px, 8vw, 72px);
    font-weight: 700;
    color: #ffffff;
    text-align: center;
    transform: translateY(9vh);
  }

  .birou-container {
    background: rgba(255, 255, 255, 0.9);
    padding: 36px;
    max-width: 900px;
    border-radius: 5px;
    backdrop-filter: blur(10px);
    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
    color: #000;
  }

  .birou-p { 
    font-size: 19px; 
    margin-bottom: 24px; 
    line-height: 1.6;
    text-align: justify;
  }

  .birou-p:last-child {
    margin-bottom: 0;
  }

  .serviciu-box {
    border: 1px solid #eee;
    padding: 25px;
    background: #fff;
    transition: 0.3s;
  }

  /* --- CONTACT GRID --- */
  .contact-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
    width: 100%;
    max-width: 1100px;
    margin-top: 40px;
  }

  .form-input {
    padding: 9px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-family: inherit;
    width: 100%;
    font-size: 19px;
  }

  .contact-btn { 
    background: ${ACCENT};
    color: #fff; 
    padding: 10px;
    border-radius: 8px;
    border: none;
    font-weight: 600;
    cursor: pointer;
    transition: 0.3s;
    font-size: 19px;
  }

  .lang-fab {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 1000;
    background: #fff;
    color: #000;
    border: 1px solid #eee;
    width: 45px;
    height: 45px;
    border-radius: 50%;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 10px rgba(0,0,0,0.1);
  }

  @media (max-width: 900px) {
    .nav-links { display: none; }
    .burger { display: flex; }
    .contact-grid { 
      grid-template-columns: 1fr; 
      display: flex; 
      flex-direction: column-reverse; 
    }
    .servicii-layout-wrapper { flex-direction: column; }
    .logo-main { color: #000000; }
    nav.scrolled .logo-main { color: #111; }
  }
`;

const T_DATA = {
  ro: {
    birou: "Studio", servicii: "Servicii", contact: "Contact", logoSub: "arhitectura si design",
    hTitle: "Concretizăm dorințele tale",
    birouP: [
      "VRTX Studio reprezintă punctul de întâlnire dintre rigoarea tehnică și viziunea creativă, fiind un birou de arhitectură consolidat prin proiectarea și implementarea a peste 250.000 mp de spații complexe. Expertiza noastră acoperă un spectru larg, de la dinamica segmentului instituțional și comercial, până la precizia necesară proiectelor industriale și rezidențiale.", 
      "În ultimii ani, am transformat provocările tehnice în soluții durabile, gestionând fluxuri tehnologice complexe pentru unități de producție, dezvoltând infrastructură educațională și culturală de ultimă generație sau integrând sustenabilitatea în parcuri fotovoltaice și ansambluri rezidentiale de anvergură. Această diversitate ne oferă capacitatea de a aborda fiecare temă de proiectare cu o înțelegere profundă a contextului și a eficienței necesare.",
      "Credem că fiecare spațiu are propriul său mod de a prinde viață. La VRTX Studio, nu doar proiectăm clădiri, ci ghidăm aspirațiile clienților noștri către o realitate concretă, unde echilibrul dintre funcționalitate, estetică și durabilitate devine un standard natural."
    ],
    servIntro: [
      { text: "Procesul nostru de lucru este conceput pentru a vă oferi claritate și siguranță în fiecare etapă, transformând viziunea dumneavoastră într-un proiect viabil." },
      { bold: "Consultanță Inițială:", text: "Orice proiect de succes începe cu o discuție deschisă, menită să exploreze potențialul ideii dumneavoastră. Acest prim pas este esențial pentru a defini direcția strategică și pentru a înțelege modul în care VRTX Studio poate aduce valoare adăugată investiției. În cazul în care obiectivele dumneavoastră necesită competențe specifice din afara ariei noastre, vă vom îndruma cu transparență către cei mai potriviți parteneri." },
      { bold: "Propunerea și Structura:", text: "În urma analizei inițiale, elaborăm o ofertă personalizată ce cuprinde pachete de servicii adaptate nevoilor dumneavoastră, însoțite de o estimare transparentă a costurilor și a termenelor de livrare. Colaborarea este structurată cronologic, pe etape clare, oferindu-vă un control deplin asupra evoluției proiectului și a resurselor alocate." },
      { bold: "Parteneriat și Predictibilitate:", text: "Fiecare etapă de proiectare este definită prin termene de execuție și condiții comerciale sigure. De la proiectarea completă până la consultanța specializată, procesul nostru de facturare este flexibil și adaptat standardelor moderne, asigurând o colaborare fluidă și lipsită de incertitudini." }
    ],
    servList: [
      { t: "Arhitectură", d: "Concept și Integrare (SF/DALI); Documentație pentru avizare (DTAC/DTAD); Proiect Tehnic și Detalii de Execuție (PTh+DE); Urmărire de Șantier (DTOE)" },
      { t: "Design", d: "Design de Interior; Design de Produs; Desen Tehnic și Randări pentru Patentare" },
      { t: "Consultanță", d: "Parteneriat Decizional; Analiză Imobiliară și Integrare Urbană; Consultanță pentru Remedieri; Abordări Decizionale" }
    ],
    cSubtitle: "Orice proiect de succes începe cu o decizie informată. Suntem aici pentru a vă oferi claritatea tehnică și creativă necesară investiției dumneavoastră.",
    footerL: "© 2026 VRTX Studio",
    footerC: "Zero cookies. Respectăm intimitatea.",
    send: "Trimite"
  },
  en: {
    birou: "Studio", servicii: "Services", contact: "Contact", logoSub: "architecture and design",
    hTitle: "Realize what you need",
    birouP: [
      "VRTX Studio is the intersection of technical rigor and creative vision - an architectural practice built on the experience of designing and developing over 250,000 sqm of complex built environments. Our expertise spans a wide spectrum, ranging from the dynamic requirements of the institutional and commercial sectors to the precision demanded by industrial and residential projects.",
      "In recent years, we have specialized in transforming technical challenges into sustainable solutions. Our portfolio includes managing complex technological workflows for industrial production units, developing next-generation educational and cultural infrastructure, and integrating large-scale sustainability through photovoltaic parks and expansive residential developments. This diversity allows us to approach every design brief with a profound understanding of both context and efficiency.",
      "We believe that every space has its own way of coming to life. At VRTX Studio, we don’t just design buildings; we guide our clients aspirations toward a tangible reality, where the balance between functionality, aesthetics, and durability becomes a natural standard."
    ],
    servIntro: [
      { text: "Our workflow is designed to provide clarity and confidence at every stage, turning your vision into a viable, high-quality project." },
      { bold: "Initial Consultation:", text: "Every successful project begins with an open dialogue aimed at exploring the potential of your idea. This first step is essential for defining the strategic direction and understanding how VRTX Studio can add value to your investment. Should your objectives require specific expertise outside our scope, we will transparently guide you toward the most suitable partners." },
      { bold: "Proposal and Structure:", text: "Following our initial analysis, we develop a tailored proposal featuring service packages adapted to your needs, accompanied by a transparent estimate of costs and delivery timelines. Our collaboration is structured chronologically, in clear phases, giving you full control over the project's evolution and resource allocation." },
      { bold: "Partnership and Predictability:", text: "Each design phase is defined by specific deadlines and secure commercial terms. From comprehensive design to specialized consulting, our billing process is flexible and adapted to modern standards, ensuring a fluid collaboration free of uncertainties." }
    ],
    servList: [
      { t: "Architecture", d: "Concept & Integration (SF/DALI); Permitting Documentation (DTAC/DTAD); Technical Project & Execution Details (PTh+DE); Site Supervision (DTOE)" },
      { t: "Design", d: "Interior Design; Product Design; Technical Drawing & Patent Rendering" },
      { t: "Consultancy", d: "Decision-Making Partnership; Real Estate Analysis & Urban Integration; Remedial Consultancy; Strategic Guidance" }
    ],
    cSubtitle: "Every successful project starts with an informed decision. We are here to provide the technical and creative clarity your investment needs.",
    footerL: "© 2026 VRTX Studio",
    footerC: "Cookie-free. Privacy-first.",
    send: "Send"
  }
};

export default function App() {
  const [lang, setLang] = useState("ro");
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const [showSuccess, setShowSuccess] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      const serviciiSection = document.getElementById('servicii');
      if (serviciiSection) {
        const rect = serviciiSection.getBoundingClientRect();
        setScrolled(rect.top <= 60);
      }
      const sectionIds = ["hero", "birou", "servicii", "contact"];
      const clipSections = document.querySelectorAll('.clip-section');
      clipSections.forEach(el => {
        const rect = el.getBoundingClientRect();
        const hiddenTop = Math.max(0, 60 - rect.top);
        el.style.clipPath = `inset(${hiddenTop}px 0 0 0)`;
      });
    
      const sections = document.querySelectorAll('.clip-section');
      sections.forEach(el => {
        const rect = el.getBoundingClientRect();
        const hiddenTop = Math.max(0, 60 - rect.top);
        el.style.clipPath = `inset(${hiddenTop}px 0 0 0)`;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const T = T_DATA[lang];
  const scrollTo = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <style>{css}</style>
      

      <div style={{ backgroundImage: `url(${heroBg})`, position: 'fixed', inset: 0, zIndex: -1, backgroundSize: 'cover', backgroundPosition: 'center' }} />

      <nav className={scrolled ? "scrolled" : ""}>
        <div className="logo-container" onClick={() => scrollTo("hero")}>
          <div className="logo-main"><strong>VRTX</strong> Studio</div>
          <div className="logo-sub">{T.logoSub}</div>
        </div>
        <div className="nav-links">
          <a onClick={() => scrollTo("birou")}>{T.birou}</a>
          <a onClick={() => scrollTo("servicii")}>{T.servicii}</a>
          <a onClick={() => scrollTo("contact")}>{T.contact}</a>
        </div>
        <div className={`burger ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen(!menuOpen)}>
          <span style={{background: (scrolled || menuOpen) ? '#000' : '#000000'}}></span>
          <span style={{background: (scrolled || menuOpen) ? '#000' : '#000000'}}></span>
          <span style={{background: (scrolled || menuOpen) ? '#000' : '#000000'}}></span>
        </div>
      </nav>

      <div className={`mobile-menu ${menuOpen ? 'open' : ''}`}>
        <a onClick={() => scrollTo("birou")}>{T.birou}</a>
        <a onClick={() => scrollTo("servicii")}>{T.servicii}</a>
        <a onClick={() => scrollTo("contact")}>{T.contact}</a>
      </div>

      <div className="content-mask">
        {/* HERO */}
        <section id="hero" className="page-section clip-section" style={{background: 'transparent'}}>
          <div className="hero-content" onClick={() => scrollTo("contact")} style={{cursor:'pointer'}}>
            <h1 className="hero-title">{T.hTitle}</h1>
          </div>
        </section>

        {/* BIROU */}
        <section id="birou" className="page-section clip-section" style={{background: 'transparent'}}>
           <div className="birou-container">
            <h2 style={{
              fontSize: '42px',
              marginBottom: '20px',              
              fontWeight:'700',
              color: ACCENT2,
              textAlign: 'center',
            }}>
                {T.birou}</h2>
            {T.birouP.map((p, i) => <p key={i} className="birou-p">{p}</p>)}
           </div>
        </section>

        {/* SERVICII */}
        <section id="servicii" className="page-section clip-section" style={{background: 'rgba(255, 255, 255, 0.95)'}}>
          <div style={{ width: '100%', maxWidth: '1100px' }}>
            <h2 style={{
              fontSize: '42px',
              color: ACCENT2,
              marginBottom: '40px',
              textAlign: 'center'}}>{T.servicii}</h2>
            <div className="servicii-layout-wrapper" style={{display:'flex', gap:'50px'}}>
              <div style={{flex: 1.3}}>
                {T.servIntro.map((item, index) => (
                  <p key={index} style={{ fontSize: '18px', marginBottom: '20px', lineHeight: '1.7', color: '#444' }}>
                    <strong style={{color: ACCENT}}>{item.bold} </strong>{item.text}
                  </p>
                ))}
              </div>
              <div style={{flex: 0.7, display:'flex', flexDirection:'column', gap:'15px'}}>
                {T.servList.map((s, i) => (
                  <div key={i} className="serviciu-box">
                    <h3 style={{ fontSize: '18px', marginBottom: '10px', color: ACCENT }}>{s.t}</h3>
                    <div style={{ textAlign: 'left' }}>
                      {s.d.split(';').map((punct, idx) => (
                        <p key={idx} style={{ marginBottom: '5px', fontSize: '14px', color: '#666' }}>• {punct.trim()}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CONTACT */}
        {/* CONTACT */}
        <section id="contact" className="page-section clip-section" style={{background: '#fff', padding: '100px 24px'}}>
          <div style={{ width: '100%', maxWidth: '1100px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '40px',
              marginBottom: '60px',
              color: ACCENT2,
              textAlign: 'center' }}>{T.contact}</h2>
            
            <div className="contact-grid">
              {/* STÂNGA: HARTĂ ȘI BRANDING */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ width:'100%', height:'412px', marginTop:'5px', background:'#eee', borderRadius: '6px', overflow: 'hidden'}}>
                  <iframe title="map" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2848.566144510065!2d26.1105473!3d44.4420749!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x40b1ff359df37f81%3A0x6a2c22226f905206!2sCalea%20Mo%C8%99ilor%20280%2C%20Bucure%C8%99ti!5e0!3m2!1sro!2sro!4v1700000000000" width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy"></iframe>
                </div>
                
                <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', paddingTop: '30px' }}>
                  <h3 style={{color: ACCENT2, margin: 0, fontWeight: '700', fontSize: '44px', lineHeight: '1'}}>VRTX Studio</h3>
                  <div style={{ fontSize: '18px', lineHeight: '1.1', color: '#000000', textAlign: 'right' }}>
                    <p>Calea Moșilor 280, București</p>
                    <p>office@vrtx.ro | (+4) 0755 121 595</p>
                  </div>
                </div>
              </div>

              {/* DREAPTA: FORMULAR FUNCTIONAL */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {showSuccess ? (
                  <div style={{ 
                    height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', 
                    alignItems: 'center', textAlign: 'center', padding: '40px', background: '#f9f9f9',
                    borderRadius: '8px', border: `2px dashed ${ACCENT}`
                  }}>
                    <h3 style={{ color: ACCENT, fontSize: '24px', marginBottom: '10px' }}>
                      {lang === "ro" ? "Mesaj Trimis!" : "Message Sent!"}
                    </h3>
                    <p style={{ fontSize: '18px', color: '#555' }}>
                      {lang === "ro" 
                        ? "Vă mulțumim! Vom răspunde solicitării dumneavoastră în cel mai scurt timp." 
                        : "Thank you! We will get back to you as soon as possible."}
                    </p>
                    <button 
                      onClick={() => setShowSuccess(false)}
                      style={{ marginTop: '20px', background: 'none', border: 'none', color: ACCENT, cursor: 'pointer', fontWeight: '600', textDecoration: 'underline' }}
                    >
                      {lang === "ro" ? "Trimite alt mesaj" : "Send another message"}
                    </button>
                  </div>
                ) : (
                  <form 
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target);
                      try {
                        const response = await fetch("contact.php", { method: "POST", body: formData });
                        if (response.ok) { setShowSuccess(true); e.target.reset(); }
                        else { alert("Eroare la trimitere."); }
                      } catch (error) { alert("Eroare de conexiune."); }
                    }}
                    style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                  >
                    <div style={{ height: '80px', marginBottom: '15px' }}>
                      <p style={{ color: ACCENT, fontWeight: 600, fontSize: '20px', lineHeight: '1.2' }}>{T.cSubtitle}</p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', flexGrow: 1 }}>
                      <input type="text" name="name" placeholder={lang === "ro" ? "Nume" : "Name"} className="form-input" required />
                      <input type="email" name="email" placeholder="Email" className="form-input" required />
                      <input type="hidden" name="service" value="Contact General" />
                      <textarea name="message" placeholder={lang === "ro" ? "Mesaj" : "Message"} className="form-input" style={{height: '200px', resize: 'none'}} required></textarea>
                    </div>
                    <button type="submit" className="contact-btn" style={{ marginTop: '30px' }}>
                      {T.send}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>

          <footer style={{ width: '100%', maxWidth: '1100px', margin: '80px auto 0 auto', paddingTop: '20px', borderTop: '1px solid #eee', textAlign: 'center', fontSize: '16px', color: ACCENT2 }}>
            <p>{T.footerL} | {T.footerC}</p>
          </footer>
        </section>
      </div>

      <button className="lang-fab" onClick={() => setLang(l => l === "ro" ? "en" : "ro")}>
        {lang.toUpperCase()}
      </button>
    </>
  );
}